import { Socket, io } from 'socket.io-client';
import { create } from 'zustand';

import { DeviceDto } from '~modules/device/model/dto.ts';
import { ImportJobDto, ImportJobResponse, isTerminalJobStatus } from '~modules/tabel/model/import-job.dto.ts';
import { config } from '~shared/config';

/**
 * Сколько ждем завершения джобы импорта.
 * 5 минут - с запасом на cron (~171 с) и последующую генерацию табеля.
 */
export const WS_JOB_TIMEOUT_MS = 300_000;

/** Текст для пользователя, когда джоба не уложилась в таймаут: сервер при этом продолжает работу */
export const WS_TIMEOUT_MESSAGE =
	'Превышено время ожидания загрузки данных. Данные могут обновляться на сервере - подождите 2-3 минуты и обновите страницу.';

/** Отличаем таймаут от прочих ошибок: у него свой текст и своя причина */
export class WsTimeoutError extends Error {
	constructor() {
		super(WS_TIMEOUT_MESSAGE);
		this.name = 'WsTimeoutError';
	}
}

/** Сообщают вызывающему, что задача принята (или что импорт уже шел) - до ее завершения */
type OnAccepted = (result: { accepted: boolean; job: ImportJobDto }) => void;

interface useTabelRegenerationStoreState {
	socket: Socket | null;
	isConnected: boolean;
	qr: string | null;
	isManuallyDisconnected: boolean;
	connect: () => void;
	importAndGenerate: (onAccepted?: OnAccepted) => Promise<ImportJobDto>;
	disconnect: () => void;
	time: Date;
	tick: () => void;
	device: DeviceDto | null;
	/** Последнее известное состояние джобы импорта - для индикации хода работы */
	job: ImportJobDto | null;
}

export const useTabelRegeneration = create<useTabelRegenerationStoreState>((set, get) => ({
	socket: null,
	isConnected: false,
	qr: null,
	time: new Date(),
	isManuallyDisconnected: false,
	device: null,
	job: null,

	connect: () => {
		const { socket: existingSocket } = get();
		if (existingSocket) {
			if (!existingSocket.connected) {
				existingSocket.connect();
			}
			return;
		}

		const newSocket = io(config.wsUrl, {
			query: { deviceNumber: 'user-device', deviceId: 'user-device-id' },
			reconnection: true,
			reconnectionAttempts: 3,
			reconnectionDelay: 2000,
			reconnectionDelayMax: 2000,
		});

		newSocket.on('connect', () => {
			console.log('✅ Connected');
			set({ isConnected: true, isManuallyDisconnected: false });
		});

		newSocket.on('disconnect', () => {
			console.warn('⚠️ Disconnected');
			set({ isConnected: false });
			if (!get().isManuallyDisconnected) {
				console.log('🔄 Attempting reconnection...');
			}
		});

		newSocket.on('connect_error', err => {
			console.error('❌ Connection error:', err);
		});

		newSocket.on('qr', msg => {
			console.log('qr', msg);
			set(state => ({ ...state, qr: msg }));
		});

		set({ socket: newSocket });
	},

	/**
	 * Запускает импорт и ждет завершения джобы, а не одного долгого ответа:
	 * сервер отвечает сразу (accepted/running), дальше состояние приходит событиями
	 * import-job, пока не станет терминальным.
	 */
	importAndGenerate(onAccepted?: OnAccepted): Promise<ImportJobDto> {
		const { socket } = get();

		if (!socket) throw new Error('нет соединения c websocket');

		return new Promise<ImportJobDto>((resolve, reject) => {
			let jobId: string | null = null;
			let isSettled = false;

			const cleanup = () => {
				clearTimeout(timer);
				socket.off('import-job', handleJob);
				socket.off('connect_error', handleConnectError);
				socket.off('connect', handleReconnect);
			};

			const settle = (finish: () => void) => {
				if (isSettled) return;
				isSettled = true;
				cleanup();
				finish();
			};

			// Событие может прийти по чужой джобе (например, по cron-генерации) - отсеиваем по id.
			// До получения ответа с id принимаем любое: своей джобы мы еще не знаем.
			const handleJob = (job?: ImportJobDto | null) => {
				if (!job) return;
				if (jobId && job.id !== jobId) return;
				set(state => ({ ...state, job }));

				if (!isTerminalJobStatus(job.status)) return;
				if (job.status === 'completed') settle(() => resolve(job));
				else settle(() => reject(new Error(job.error ?? 'Ошибка обновления данных')));
			};

			// Пока задача не принята, обрыв связи - это отказ: ждать 5 минут нечего.
			// После принятия обрывы игнорируем: сервер продолжает работу, состояние заберем при переподключении.
			const handleConnectError = (err: Error) => {
				console.error('❌ Connection error:', err);
				if (jobId) return;
				settle(() => reject(new Error('Нет связи с сервером обновления данных')));
			};

			// Переподключились посреди ожидания - события за время обрыва потеряны, запрашиваем состояние
			const handleReconnect = () => {
				if (!jobId) return;
				socket.emit('import-status', {}, (response?: ImportJobResponse) => handleJob(response?.job));
			};

			const timer = setTimeout(() => {
				settle(() => reject(new WsTimeoutError()));
			}, WS_JOB_TIMEOUT_MS);

			socket.on('import-job', handleJob);
			socket.on('connect_error', handleConnectError);
			socket.on('connect', handleReconnect);

			socket.emit(
				'import-and-generate',
				{ timestamp: new Date().toISOString() },
				(response?: ImportJobResponse) => {
					if (!response?.success || !response.job) {
						settle(() => reject(new Error(response?.error ?? 'Сервер не принял запрос на обновление')));
						return;
					}

					jobId = response.job.id;
					onAccepted?.({ accepted: response.accepted, job: response.job });
					// Джоба могла завершиться до того, как мы узнали ее id (например, чужая, уже почти готовая)
					handleJob(response.job);
				},
			);
		});
	},

	disconnect: () => {
		const { socket } = get();
		if (socket) {
			set({ isManuallyDisconnected: true, isConnected: false });
			socket.disconnect();
		}
	},

	tick: () => {
		set(old => ({ ...old, time: new Date() }));
	},
}));
